/**
 * Formatação para exibição.
 *
 * Dinheiro chega da API como STRING decimal (ADR-010). Aqui ele é FORMATADO,
 * nunca somado nem multiplicado — nenhuma aritmética monetária acontece no
 * browser.
 */

/**
 * IDIOMA E FUSO DA PESSOA (§20.5).
 *
 * A REGRA: **a moeda é do FATO, o formato é da PESSOA.**
 *
 * Trocar a interface para inglês não pode transformar R$ em US$ — o valor é um
 * fato do negócio, e a língua de quem lê não muda quanto o cliente vai pagar. O
 * idioma decide a forma do número e da data; a moeda vem do registro. Era isto
 * que estava errado: a moeda era fixa em BRL, e uma oportunidade em dólar
 * aparecia com cifrão de real — silenciosamente, e num número que alguém usa
 * para decidir.
 *
 * A preferência mora no MÓDULO, e não num contexto de React, por um motivo
 * prático: estas funções são chamadas de dezenas de telas e de funções puras que
 * não são componentes. Um contexto obrigaria a reescrever todas elas para ganhar
 * o mesmo resultado. Quem define é a sessão, quando `/auth/me` responde.
 */
type IdiomaDaTela = 'pt-BR' | 'en';

let idiomaAtivo: IdiomaDaTela = 'pt-BR';
let fusoAtivo = 'America/Sao_Paulo';

/** Cache dos formatadores: montar `Intl` a cada célula de tabela é caro. */
const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

export function definirFormatacao(preferencia: { idioma: string; fuso: string }): void {
  idiomaAtivo = preferencia.idioma === 'en' ? 'en' : 'pt-BR';
  fusoAtivo = preferencia.fuso;
  cache.clear();
}

/** Locale completo: `en` sozinho formataria número no padrão britânico. */
function locale(): string {
  return idiomaAtivo === 'en' ? 'en-US' : 'pt-BR';
}

function dinheiro(moeda: string, compacto: boolean): Intl.NumberFormat {
  const chave = `m|${locale()}|${moeda}|${compacto ? 'c' : 'p'}`;
  const existente = cache.get(chave);
  if (existente instanceof Intl.NumberFormat) return existente;
  const feito = new Intl.NumberFormat(locale(), {
    style: 'currency',
    currency: moeda,
    ...(compacto
      ? { notation: 'compact' as const, minimumFractionDigits: 0, maximumFractionDigits: 1 }
      : { minimumFractionDigits: 2 }),
  });
  cache.set(chave, feito);
  return feito;
}

function data(opcoes: Intl.DateTimeFormatOptions, chave: string): Intl.DateTimeFormat {
  const completa = `d|${locale()}|${fusoAtivo}|${chave}`;
  const existente = cache.get(completa);
  if (existente instanceof Intl.DateTimeFormat) return existente;
  const feito = new Intl.DateTimeFormat(locale(), { ...opcoes, timeZone: fusoAtivo });
  cache.set(completa, feito);
  return feito;
}

const OPCOES_DATA: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

const OPCOES_DATA_HORA: Intl.DateTimeFormatOptions = {
  ...OPCOES_DATA,
  hour: '2-digit',
  minute: '2-digit',
};

const OPCOES_DIA_MES: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' };

/** A moeda da operação. Usada quando o registro não declara a sua. */
export const MOEDA_PADRAO = 'BRL';

/** Data PURA, sem hora: `AAAA-MM-DD`. */
const SOMENTE_DATA = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Nome curto do mês, para `formatDayMonth` de data pura.
 *
 * Escrito à mão porque `Intl` só formata a partir de um instante, e é justamente a
 * conversão para instante que introduz o erro que esta função evita.
 */
const MES_CURTO = [
  'jan.',
  'fev.',
  'mar.',
  'abr.',
  'mai.',
  'jun.',
  'jul.',
  'ago.',
  'set.',
  'out.',
  'nov.',
  'dez.',
];

export function formatMoney(
  value: string | null | undefined,
  /** A moeda do REGISTRO. O idioma da pessoa não a altera. */
  moeda: string = MOEDA_PADRAO,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? dinheiro(moeda, false).format(numeric) : '—';
}

export function formatMoneyCompact(
  value: string | null | undefined,
  moeda: string = MOEDA_PADRAO,
): string {
  if (value === null || value === undefined || value === '') return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  // O zero compacto sai do próprio formatador: escrever "R$ 0" à mão fixava a
  // moeda e o idioma de uma vez só.
  return dinheiro(moeda, true).format(numeric);
}

/**
 * Data para exibição.
 *
 * DEFEITO QUE ISTO CORRIGE, e que atingia toda data pura do sistema:
 *
 * As colunas de DATA (previsão de fechamento, vigência de contrato, data de revisão
 * do congelamento) chegam do servidor como `AAAA-MM-DD`. `new Date('2026-08-10')` é
 * interpretado pelo JavaScript como meia-noite **UTC**; formatado no fuso de São
 * Paulo (UTC−3), isso vira **09/08/2026**. Toda data pura aparecia um dia antes —
 * e 01/01/2026 aparecia como 31/12/2025, virando o ano.
 *
 * O erro não é de formatação, é conceitual: uma data sem hora NÃO tem fuso. "10 de
 * agosto" é 10 de agosto em qualquer lugar do mundo, e convertê-la entre fusos é a
 * própria fonte do bug. Por isso a data pura é formatada a partir das PARTES, sem
 * passar por `Date`.
 *
 * Já um INSTANTE (`2026-08-10T14:30:00Z`) tem fuso de verdade, e aí a conversão para
 * São Paulo é o comportamento certo — é o que a segunda metade da função faz.
 */
export function formatDate(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';

  const puro = SOMENTE_DATA.exec(value);
  if (puro !== null) {
    const [, ano, mes, dia] = puro;
    // A ordem acompanha o idioma. Manter dia/mês em inglês produziria a pior
    // ambiguidade possível: 08/09 é lido de dois jeitos e ninguém percebe.
    return idiomaAtivo === 'en' ? `${mes}/${dia}/${ano}` : `${dia}/${mes}/${ano}`;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : data(OPCOES_DATA, 'data').format(date);
}

export function formatDateTime(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : data(OPCOES_DATA_HORA, 'data-hora').format(date);
}

/**
 * Só a hora, no fuso da pessoa (CERES-017 §2.4).
 *
 * Existia como `toLocaleTimeString('pt-BR')` solto numa tela de operação, o
 * que fixava o idioma e ignorava o fuso escolhido na conta. Formatar hora é
 * decisão do módulo, como todo o resto.
 */
export function formatTime(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : data({ hour: '2-digit', minute: '2-digit', second: '2-digit' }, 'hora').format(date);
}

/** "quinta-feira, 17 de setembro" — o dia de hoje no cabeçalho da home, no fuso da pessoa. */
export function formatDiaPorExtenso(agora: Date = new Date()): string {
  return data({ weekday: 'long', day: 'numeric', month: 'long' }, 'dia-extenso').format(agora);
}

/** Dia e mês curto. Mesma distinção de `formatDate`: data pura não tem fuso. */
export function formatDayMonth(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';

  const puro = SOMENTE_DATA.exec(value);
  if (puro !== null) {
    const [, , mes, dia] = puro;
    return `${dia} de ${MES_CURTO[Number(mes) - 1] ?? mes}`;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : data(OPCOES_DIA_MES, 'dia-mes').format(date);
}

/** `AAAA-MM` -> `out/2026`. */
export function formatMonth(value: string): string {
  const [year, month] = value.split('-');
  if (year === undefined || month === undefined) return value;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return new Intl.DateTimeFormat(locale(), { month: 'short', year: 'numeric' }).format(date);
}

/** Distância relativa em português, para prazos e linha do tempo. */
export function formatRelative(value: string | null | undefined, now = new Date()): string {
  if (value === null || value === undefined || value === '') return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / 86_400_000);

  if (diffDays === 0) return 'hoje';
  if (diffDays === 1) return 'amanhã';
  if (diffDays === -1) return 'ontem';
  if (diffDays > 1 && diffDays <= 30) return `em ${diffDays} dias`;
  if (diffDays < -1 && diffDays >= -30) return `há ${Math.abs(diffDays)} dias`;

  const diffMonths = Math.round(diffDays / 30);
  if (diffMonths > 0) return `em ${diffMonths} ${diffMonths === 1 ? 'mês' : 'meses'}`;
  return `há ${Math.abs(diffMonths)} ${Math.abs(diffMonths) === 1 ? 'mês' : 'meses'}`;
}

/**
 * CNPJ formatado a partir de 14 posições. Aceita também o CNPJ ALFANUMÉRICO
 * (IN RFB 2.229/2024): letras nas 12 primeiras posições, verificadores
 * numéricos. A validação é do servidor; aqui só se formata.
 */
export function formatCnpj(value: string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const digits = value.toUpperCase().replace(/[^0-9A-Z]/g, '');
  if (digits.length !== 14) return value;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
}

/** Máscara progressiva de CNPJ, para digitação no formulário. */
export function maskCnpj(value: string): string {
  const digits = value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 14);
  let output = digits;
  if (digits.length > 2) output = `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length > 5) output = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length > 8) {
    output = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }
  if (digits.length > 12) {
    output = `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }
  return output;
}

/**
 * Percentual no idioma da pessoa.
 *
 * `casasDecimais` existe porque nem todo percentual é indicador de painel: uma
 * taxa de resposta de 35,7% arredondada para 36% perde a diferença entre duas
 * cadências. O padrão continua inteiro — quem precisa da casa decimal pede.
 */
export function formatPercent(value: number | null | undefined, casasDecimais = 0): string {
  if (value === null || value === undefined) return '—';
  const numero = new Intl.NumberFormat(locale(), {
    minimumFractionDigits: casasDecimais,
    maximumFractionDigits: casasDecimais,
  }).format(value);
  return `${numero}%`;
}

/** Iniciais para avatar textual. */
export function initials(name: string): string {
  const parts = name
    .replace(/\[DEMO\]\s*/g, '')
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Trunca preservando palavra. */
export function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut}…`;
}

/** Valor para `<input type="datetime-local">` a partir de ISO, no fuso local. */
export function toDateTimeLocalValue(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** ISO em UTC a partir do valor de `<input type="datetime-local">`. */
export function fromDateTimeLocalValue(value: string): string | null {
  if (value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * "1 parada" / "3 paradas": a palavra concorda com o número (0.131.0).
 * Zero leva o plural, como na fala ("0 oportunidades").
 */
export function contagem(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}
